import { useForm } from "@mantine/form";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@heroui/react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeDateRangePicker } from "@/Components/NextBase/LimeDateRangePicker";
import { Trans } from "@lingui/react/macro";
import { useEffect, useMemo, useRef } from "react";
import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import dayjs from "dayjs";
import { api } from "@/lib/api-client";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeTextarea } from "@/Components/NextBase/LimeTextarea";
import { CalendarDate } from "@internationalized/date";
import { getSubscriptionFreezeReasonOptions } from "@/constants/freezeReasons";
import { SubscriptionFreezeReason } from "@/server-types";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { addToast } from "@heroui/react";

const toCalendarDateUtc = (date: Date): CalendarDate => {
  const utcDate = dayjs(date).utc();
  return new CalendarDate(utcDate.year(), utcDate.month() + 1, utcDate.date());
};

const formatCalendarDate = (date: CalendarDate, locale: string): string =>
  new Date(date.year, date.month - 1, date.day).toLocaleDateString(locale);

type FreezeFormValues = {
  freezeStart: CalendarDate;
  freezeEnd: CalendarDate;
  freezeReason: SubscriptionFreezeReason;
  notes: string;
  deleteCustomerAppointmentsInFreezePeriod: boolean;
  restoreAppointmentsInOldRange: boolean;
  deleteAppointmentsInNewRange: boolean;
};

type CustomerSubscriptionFreezeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
  scheduledFreezeId?: number | null; // number for update, null for create
};

export const CustomerSubscriptionFreezeModal = ({
  isOpen,
  onClose,
  customerSubscriptionId,
  scheduledFreezeId = null,
}: CustomerSubscriptionFreezeModalProps) => {
  const { locale } = i18n;
  const {
    mutateAsync: freezeSubscription,
    isPending: isFreezingSubscription,
    processedErrorMessage: freezeSubscriptionError,
  } = api.customer.usePostCustomerSubscriptionFreeze();

  const { data: customerSubscriptionResponse } =
    api.customer.useGetCustomerSubscription({ customerSubscriptionId });

  const isEditing = scheduledFreezeId != null;

  const form = useForm<FreezeFormValues>({
    initialValues: {
      freezeStart: new CalendarDate(
        dayjs().year(),
        dayjs().month() + 1,
        dayjs().date(),
      ),
      freezeEnd: new CalendarDate(
        dayjs().add(1, "week").year(),
        dayjs().add(1, "week").month() + 1,
        dayjs().add(1, "week").date(),
      ),
      freezeReason: "Vacation",
      notes: "",
      deleteCustomerAppointmentsInFreezePeriod: true,
      restoreAppointmentsInOldRange: true,
      deleteAppointmentsInNewRange: true,
    },
    validate: {
      freezeEnd: (value) =>
        dayjs(value.toString()).isBefore(dayjs(), "day")
          ? t`Datum konca zamrznitve ne sme biti v preteklosti`
          : null,
    },
  });

  const {
    mutateAsync: updateScheduledFreeze,
    isPending: isUpdatingScheduledFreeze,
    processedErrorMessage: updateScheduledFreezeError,
  } = api.customer.usePutScheduledSubscriptionFreeze();

  const { data: scheduledFreezeResponse } =
    api.customer.useGetScheduledSubscriptionFreeze({
      customerSubscriptionId,
      scheduledFreezeId,
    });

  const initialRangeRef = useRef<{
    start: CalendarDate;
    end: CalendarDate;
  } | null>(null);

  useEffect(() => {
    if (!isEditing || !scheduledFreezeResponse) return;
    const { scheduledFreeze } = scheduledFreezeResponse;
    const freezeStart = toCalendarDateUtc(
      new Date(scheduledFreeze.freezeStart),
    );
    const freezeEnd = toCalendarDateUtc(new Date(scheduledFreeze.freezeEnd));

    form.setValues({
      freezeStart,
      freezeEnd,
      freezeReason: scheduledFreeze.freezeReason,
      notes: scheduledFreeze.notes ?? "",
      restoreAppointmentsInOldRange: true,
      deleteAppointmentsInNewRange: true,
    });
    initialRangeRef.current = {
      start: freezeStart,
      end: freezeEnd,
    };
  }, [scheduledFreezeId, scheduledFreezeResponse]);

  const freezePeriodLabel = `${formatCalendarDate(form.values.freezeStart, locale)} 00:00 - ${formatCalendarDate(form.values.freezeEnd, locale)} 23:59`;

  const dateRangeChanged = useMemo(() => {
    if (!isEditing || !initialRangeRef.current) return false;
    return (
      form.values.freezeStart.toString() !==
        initialRangeRef.current.start.toString() ||
      form.values.freezeEnd.toString() !==
        initialRangeRef.current.end.toString()
    );
  }, [isEditing, form.values.freezeStart, form.values.freezeEnd]);

  const submitErrorMessage = isEditing
    ? updateScheduledFreezeError
    : freezeSubscriptionError;

  const handleSubmit = async (values: FreezeFormValues) => {
    try {
      if (isEditing) {
        await updateScheduledFreeze({
          customerSubscriptionId,
          scheduledFreezeId,
          body: {
            freezeStart: values.freezeStart.toString(),
            freezeEnd: values.freezeEnd.toString(),
            freezeReason: values.freezeReason,
            notes: values.notes,
            restoreAppointmentsInOldRange: values.restoreAppointmentsInOldRange,
            deleteAppointmentsInNewRange: values.deleteAppointmentsInNewRange,
          },
        });

        addToast({
          title: t`Načrtovana zamrznitev posodobljena`,
          description: t`Načrtovana zamrznitev je bila uspešno posodobljena`,
          color: "success",
        });
      } else {
        await freezeSubscription({
          customerSubscriptionId,
          body: {
            freezeStart: values.freezeStart.toString(),
            freezeEnd: values.freezeEnd.toString(),
            freezeReason: values.freezeReason,
            notes: values.notes.trim(),
            deleteCustomerAppointmentsInFreezePeriod:
              values.deleteCustomerAppointmentsInFreezePeriod,
          },
        });

        addToast({
          title: t`Paket zamrznjen`,
          description: t`Paket je bil uspešno zamrznjen`,
          color: "success",
        });
      }

      handleClose();
    } catch (_error) {
      return;
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const freezeReasonOptions = getSubscriptionFreezeReasonOptions();

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={handleClose}
      data-identifier="freeze-modal"
    >
      <ModalContent>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <ModalHeader>
            <div className="flex flex-col justify-between">
              {scheduledFreezeId ? (
                <Trans>Uredi načrtovano zamrznitev</Trans>
              ) : (
                <Trans>Zamrznitev paketa</Trans>
              )}
              <p className="text-xs font-medium">
                {customerSubscriptionResponse?.customerSubscription.variation
                  ?.name ?? ""}
              </p>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <LimeSelect
                data-identifier="freeze-reason-select"
                labelPlacement="outside"
                variant="bordered"
                label={t`Razlog zamrznitve`}
                placeholder={t`Izberite razlog za zamrznitev paketa`}
                selectedKeys={[form.values.freezeReason]}
                onSingleItemChange={(value) => {
                  form.setFieldValue(
                    "freezeReason",
                    value as SubscriptionFreezeReason,
                  );
                }}
                items={freezeReasonOptions.map((option) => ({
                  label: option.label,
                  key: option.value,
                }))}
              />
              <LimeDateRangePicker
                labelPlacement="outside"
                variant="bordered"
                label={t`Obdobje zamrznitve`}
                value={{
                  start: form.values.freezeStart,
                  end: form.values.freezeEnd,
                }}
                error={form.errors.freezeStart ?? form.errors.freezeEnd}
                onChange={(range) => {
                  if (!range) return;
                  form.setValues({
                    ...form.values,
                    freezeStart: range.start as CalendarDate,
                    freezeEnd: range.end as CalendarDate,
                  });
                }}
                description={
                  <Trans>Zamrznitev bo veljala: {freezePeriodLabel}</Trans>
                }
              />

              <LimeTextarea
                variant="bordered"
                label={t`Opombe`}
                {...form.getInputProps("notes")}
                placeholder={t`Vnesite dodatne opombe (neobvezno)`}
              />
              {!isEditing && (
                <LimeSwitch
                  size="sm"
                  {...form.getInputProps(
                    "deleteCustomerAppointmentsInFreezePeriod",
                    { type: "checkbox" },
                  )}
                >
                  <Trans>Odpovej termine stranke v obdobju zamrznitve</Trans>
                </LimeSwitch>
              )}
              {isEditing && dateRangeChanged && (
                <div className="space-y-2">
                  <LimeSwitch
                    size="sm"
                    {...form.getInputProps("restoreAppointmentsInOldRange", {
                      type: "checkbox",
                    })}
                  >
                    <Trans>
                      Vrni termine stranke s starega obdobja zamrznitve
                    </Trans>
                  </LimeSwitch>
                  <LimeSwitch
                    size="sm"
                    {...form.getInputProps("deleteAppointmentsInNewRange", {
                      type: "checkbox",
                    })}
                  >
                    <Trans>
                      Odpovej termine stranke v novem obdobju zamrznitve
                    </Trans>
                  </LimeSwitch>
                </div>
              )}
              <LimeAlert message={submitErrorMessage} />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={handleClose}
              isDisabled={isFreezingSubscription || isUpdatingScheduledFreeze}
            >
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              data-identifier="freeze-submit-btn"
              color="primary"
              variant="solid"
              type="submit"
              isLoading={isFreezingSubscription || isUpdatingScheduledFreeze}
            >
              {isEditing ? <Trans>Posodobi</Trans> : <Trans>Zamrzni</Trans>}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </LimeModal>
  );
};
